// Platform Admin / Control Center API (Tier 5.6).
//
// `apiFetch` returns the raw response envelope ({ success, message, data }); this
// layer unwraps `.data` and maps snake_case → camelCase, per the settled
// api-envelope convention (see features/auth/api.ts).

import { apiFetch } from "@/lib/api";
import type {
  Announcements,
  Broadcast,
  BroadcastSeverity,
  FileListing,
  MediaAsset,
  MediaAssetInput,
  SystemEvent,
  SystemEventInput,
  SystemEventKind,
  SystemSetting,
  WaidsMapping,
  WhatsNew,
  WhatsNewArchiveEntry,
} from "./types";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

// --- mappers -----------------------------------------------------------------

interface ApiSystemEvent {
  id: string;
  kind: SystemEventKind;
  display_seq: number;
  event_date: string | null;
  event_time: string;
  title: string;
  href: string;
  detail: string;
  badge: string;
  new_tab: boolean;
  password_protected: boolean;
  open_password: string;
}

function toSystemEvent(a: ApiSystemEvent): SystemEvent {
  return {
    id: a.id,
    kind: a.kind,
    displaySeq: a.display_seq,
    eventDate: a.event_date,
    eventTime: a.event_time,
    title: a.title,
    href: a.href,
    detail: a.detail,
    badge: a.badge,
    newTab: a.new_tab,
    passwordProtected: a.password_protected,
    openPassword: a.open_password,
  };
}

function fromSystemEventInput(input: SystemEventInput): Record<string, unknown> {
  return {
    event_date: input.eventDate ?? null,
    event_time: input.eventTime ?? "",
    title: input.title,
    href: input.href ?? "",
    detail: input.detail ?? "",
    badge: input.badge ?? "",
    new_tab: input.newTab ?? false,
    password_protected: input.passwordProtected ?? false,
    open_password: input.openPassword ?? "",
  };
}

interface ApiMediaAsset {
  id: string;
  parent: string | null;
  display_seq: number;
  asset_type: MediaAsset["assetType"];
  symlink_target: string | null;
  description: string;
  slug: string;
  video_id: string;
  href: string;
  detail: string;
}

function toMediaAsset(a: ApiMediaAsset): MediaAsset {
  return {
    id: a.id,
    parent: a.parent,
    displaySeq: a.display_seq,
    assetType: a.asset_type,
    symlinkTarget: a.symlink_target,
    description: a.description,
    slug: a.slug,
    videoId: a.video_id,
    href: a.href,
    detail: a.detail,
  };
}

// --- What's New --------------------------------------------------------------

export async function getWhatsNew(): Promise<WhatsNew> {
  const { data } = await apiFetch<Envelope<{ current: string; previous: string; current_created_at: string | null }>>(
    "/platform/whatsnew/",
  );
  return { current: data.current, previous: data.previous, currentCreatedAt: data.current_created_at };
}

export async function publishWhatsNew(title: string, body: string): Promise<WhatsNew> {
  const { data } = await apiFetch<Envelope<{ current: string; previous: string; current_created_at: string | null }>>(
    "/platform/whatsnew/",
    { method: "PUT", json: { title, body } },
  );
  return { current: data.current, previous: data.previous, currentCreatedAt: data.current_created_at };
}

export async function revertWhatsNew(): Promise<void> {
  await apiFetch("/platform/whatsnew/revert/", { method: "POST" });
}

export async function clearWhatsNew(): Promise<void> {
  await apiFetch("/platform/whatsnew/clear/", { method: "POST" });
}

export async function listWhatsNewArchive(): Promise<WhatsNewArchiveEntry[]> {
  const { data } = await apiFetch<Envelope<
    { id: string; title: string; body: string; created_at: string }[]
  >>("/platform/whatsnew/archive/");
  return data.map((a) => ({ id: a.id, title: a.title, body: a.body, createdAt: a.created_at }));
}

export async function deleteWhatsNewArchive(id: string): Promise<void> {
  await apiFetch(`/platform/whatsnew/archive/${id}/`, { method: "DELETE" });
}

// --- Broadcast ---------------------------------------------------------------

export async function getBroadcast(): Promise<Broadcast> {
  const { data } = await apiFetch<Envelope<{ message: string; severity: BroadcastSeverity; is_active: boolean }>>(
    "/platform/broadcast/",
  );
  return { message: data.message, severity: data.severity, isActive: data.is_active };
}

export async function setBroadcast(message: string, severity: BroadcastSeverity): Promise<Broadcast> {
  const { data } = await apiFetch<Envelope<{ message: string; severity: BroadcastSeverity; is_active: boolean }>>(
    "/platform/broadcast/",
    { method: "PUT", json: { message, severity } },
  );
  return { message: data.message, severity: data.severity, isActive: data.is_active };
}

// --- System-event lists (events / resources / toolbox / videos) --------------

/** The path segment + optional selector query string for a list "kind". */
function listUrl(resource: string, selector?: { param: string; value: string }): string {
  const base = `/platform/${resource}/`;
  if (!selector) return base;
  return `${base}?${selector.param}=${encodeURIComponent(selector.value)}`;
}

export async function listSystemEvents(
  resource: string,
  selector?: { param: string; value: string },
): Promise<SystemEvent[]> {
  const { data } = await apiFetch<Envelope<ApiSystemEvent[]>>(listUrl(resource, selector));
  return data.map(toSystemEvent);
}

export async function createSystemEvent(
  resource: string,
  input: SystemEventInput,
  selector?: { param: string; value: string },
): Promise<SystemEvent> {
  const { data } = await apiFetch<Envelope<ApiSystemEvent>>(listUrl(resource, selector), {
    method: "POST",
    json: fromSystemEventInput(input),
  });
  return toSystemEvent(data);
}

export async function updateSystemEvent(
  resource: string,
  id: string,
  input: SystemEventInput,
): Promise<SystemEvent> {
  const { data } = await apiFetch<Envelope<ApiSystemEvent>>(`/platform/${resource}/${id}/`, {
    method: "PATCH",
    json: fromSystemEventInput(input),
  });
  return toSystemEvent(data);
}

export async function deleteSystemEvent(resource: string, id: string): Promise<void> {
  await apiFetch(`/platform/${resource}/${id}/`, { method: "DELETE" });
}

export async function reorderSystemEvents(
  resource: string,
  ids: string[],
  selector?: { param: string; value: string },
): Promise<SystemEvent[]> {
  const url = selector
    ? `/platform/${resource}/reorder/?${selector.param}=${encodeURIComponent(selector.value)}`
    : `/platform/${resource}/reorder/`;
  const { data } = await apiFetch<Envelope<ApiSystemEvent[]>>(url, { method: "POST", json: { ids } });
  return data.map(toSystemEvent);
}

// --- Media library -----------------------------------------------------------

export async function listMedia(): Promise<MediaAsset[]> {
  const { data } = await apiFetch<Envelope<ApiMediaAsset[]>>("/platform/media/");
  return data.map(toMediaAsset);
}

/** Create a media node. On a duplicate LINK URL the API returns 409 — the caller
 * catches the ApiError (status 409) and re-submits with `override: true`. */
export async function createMedia(input: MediaAssetInput): Promise<MediaAsset> {
  const { data } = await apiFetch<Envelope<ApiMediaAsset>>("/platform/media/", {
    method: "POST",
    json: {
      parent: input.parent ?? null,
      asset_type: input.assetType,
      description: input.description,
      slug: input.slug ?? "",
      video_id: input.videoId ?? "",
      href: input.href ?? "",
      detail: input.detail ?? "",
      override: input.override ?? false,
    },
  });
  return toMediaAsset(data);
}

export async function updateMedia(id: string, input: MediaAssetInput): Promise<MediaAsset> {
  const { data } = await apiFetch<Envelope<ApiMediaAsset>>(`/platform/media/${id}/`, {
    method: "PATCH",
    json: {
      parent: input.parent ?? null,
      asset_type: input.assetType,
      description: input.description,
      slug: input.slug ?? "",
      video_id: input.videoId ?? "",
      href: input.href ?? "",
      detail: input.detail ?? "",
    },
  });
  return toMediaAsset(data);
}

export async function deleteMedia(id: string): Promise<void> {
  await apiFetch(`/platform/media/${id}/`, { method: "DELETE" });
}

export async function symlinkMedia(id: string, parentIds: string[]): Promise<MediaAsset[]> {
  const { data } = await apiFetch<Envelope<ApiMediaAsset[]>>(`/platform/media/${id}/symlink/`, {
    method: "POST",
    json: { parent_ids: parentIds },
  });
  return data.map(toMediaAsset);
}

export async function reorderMedia(
  items: { id: string; parent: string | null; displaySeq: number }[],
): Promise<MediaAsset[]> {
  const { data } = await apiFetch<Envelope<ApiMediaAsset[]>>("/platform/media/reorder/", {
    method: "POST",
    json: { items: items.map((i) => ({ id: i.id, parent: i.parent, display_seq: i.displaySeq })) },
  });
  return data.map(toMediaAsset);
}

// --- Internal file assets ----------------------------------------------------

export async function listFiles(path = ""): Promise<FileListing> {
  const { data } = await apiFetch<Envelope<FileListing>>(
    `/platform/files/?path=${encodeURIComponent(path)}`,
  );
  return data;
}

export async function uploadFile(dir: string, file: File): Promise<void> {
  const form = new FormData();
  form.append("dir", dir);
  form.append("file", file);
  await apiFetch("/platform/files/upload/", { method: "POST", form });
}

export async function deleteFile(path: string): Promise<void> {
  await apiFetch(`/platform/files/delete/?path=${encodeURIComponent(path)}`, { method: "DELETE" });
}

/** Absolute URL to stream a published asset (used for Copy-URL + open). */
export function fileServeUrl(path: string): string {
  const base = process.env.BUN_PUBLIC_API_URL ?? "http://localhost:8000/api/v1";
  return `${base}/platform/files/serve/?path=${encodeURIComponent(path)}`;
}

// --- Transcript-check + WAIDS + system settings ------------------------------

export async function getTranscheckResult(): Promise<{ check: string; errors: string }> {
  const { data } = await apiFetch<Envelope<{ check: string; errors: string }>>("/platform/transcheck/");
  return data;
}

export async function getTranscheckRules(): Promise<string> {
  const { data } = await apiFetch<Envelope<{ rules: string }>>("/platform/transcheck/rules/");
  return data.rules;
}

export async function saveTranscheckRules(rules: string): Promise<void> {
  await apiFetch("/platform/transcheck/rules/", { method: "PUT", json: { rules } });
}

export async function getWaidsMap(): Promise<WaidsMapping[]> {
  const { data } = await apiFetch<Envelope<
    { original: string; replacement: string; display_seq: number }[]
  >>("/platform/waidsmap/");
  return data.map((m) => ({ original: m.original, replacement: m.replacement, displaySeq: m.display_seq }));
}

export async function saveWaidsMap(mappings: { original: string; replacement: string }[]): Promise<WaidsMapping[]> {
  const { data } = await apiFetch<Envelope<
    { original: string; replacement: string; display_seq: number }[]
  >>("/platform/waidsmap/", { method: "PUT", json: { mappings } });
  return data.map((m) => ({ original: m.original, replacement: m.replacement, displaySeq: m.display_seq }));
}

export async function getSystemSetting(): Promise<SystemSetting> {
  const { data } = await apiFetch<Envelope<{ eservice_down: boolean; eservice_msg: string }>>(
    "/platform/system-settings/",
  );
  return { eserviceDown: data.eservice_down, eserviceMsg: data.eservice_msg };
}

export async function saveSystemSetting(input: SystemSetting): Promise<SystemSetting> {
  const { data } = await apiFetch<Envelope<{ eservice_down: boolean; eservice_msg: string }>>(
    "/platform/system-settings/",
    { method: "PUT", json: { eservice_down: input.eserviceDown, eservice_msg: input.eserviceMsg } },
  );
  return { eserviceDown: data.eservice_down, eserviceMsg: data.eservice_msg };
}

// --- Firm-readable announcements consumer ------------------------------------

interface ApiAnnouncements {
  whatsnew: { current: string; published_at: string } | null;
  broadcast: { message: string; severity: BroadcastSeverity } | null;
  events: {
    id: string;
    event_date: string | null;
    event_time: string;
    title: string;
    href: string;
    detail: string;
    display_seq: number;
  }[];
  resources: {
    id: string;
    kind: SystemEventKind;
    title: string;
    href: string;
    detail: string;
    new_tab: boolean;
    password_protected: boolean;
    display_seq: number;
  }[];
  eservice_down: boolean;
}

export async function getAnnouncements(): Promise<Announcements> {
  const { data } = await apiFetch<Envelope<ApiAnnouncements>>("/platform/announcements/");
  return {
    whatsnew: data.whatsnew
      ? { current: data.whatsnew.current, publishedAt: data.whatsnew.published_at }
      : null,
    broadcast: data.broadcast,
    events: data.events.map((e) => ({
      id: e.id,
      eventDate: e.event_date,
      eventTime: e.event_time,
      title: e.title,
      href: e.href,
      detail: e.detail,
      displaySeq: e.display_seq,
    })),
    resources: data.resources.map((r) => ({
      id: r.id,
      kind: r.kind,
      title: r.title,
      href: r.href,
      detail: r.detail,
      newTab: r.new_tab,
      passwordProtected: r.password_protected,
      displaySeq: r.display_seq,
    })),
    eserviceDown: data.eservice_down,
  };
}

export async function dismissWhatsNew(): Promise<void> {
  await apiFetch("/platform/announcements/whatsnew/dismiss/", { method: "POST" });
}
